import {
  EditOutlined as EditOutlinedIcon,
  WorkspacePremiumOutlined as WorkspacePremiumOutlinedIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { schemeAiPalette } from "@platform/design-system";
import { ComponentType } from "react";

import { commissionFields } from "../../../../api/commissionInjection";
import { isGridDefault } from "../../utilities/benchmarkUtils";
import { isCellDisabled } from "../../utilities/buildColumns";
import { parsePlanField, qualifyFieldPath } from "../../utilities/fieldPath";
import {
  AIExtractedFieldCard,
  AIExtractedRow,
  type FieldSource,
} from "../../../AiExtractedFieldCard";
import { useFieldMetadata } from "../../context/FieldMetadataContext";

import { aiReportedNoValue } from "./aiFieldValue";
import { FieldCommentCorner } from "./FieldCommentCorner";
import { CellRendererParams } from "./types";
import { useHoverPopover } from "./useHoverPopover";

export { isGridDefault } from "../../utilities/benchmarkUtils";

const CORNER_SIZE = 9;

const HIGH_ACCURACY_TOOLTIP = "Data extracted with at least 98% confidence";

export type AiCellStatus =
  | "ai_default"
  | "ai_extracted"
  | "needs_review_auto"
  | "needs_review_manual"
  | "needs_review_comment"
  | "edited"
  | "";

export type FieldSourceMode = "ai" | "benchmark" | "none";

export interface AiFieldData {
  btxFieldName: string;
  value: string;
  pageNumber: number | null;
  justification: string;
  planName?: string;
  network: "inNetwork" | "outOfNetwork" | null;
  networkName: string | null;
  optionName: string | null;
  documentDownloadUrl: string | null;
  documentName: string | null;
  /**
   * True when this entry was fanned out to a derived indicator key (e.g. the
   * spouse/child "Benefits Included" toggles): the extraction belongs to a
   * different row and only marks provenance here, so it can never "map" into
   * this cell and must not raise a mapping error.
   */
  indicatorOnly?: boolean;
  highAccuracy?: boolean;
  /** Who produced the value; omitted means extraction, as the card reads it. */
  source?: FieldSource;
}

/**
 * Lookup map keyed by `btxIdentifier::btxFieldName` or
 * `btxIdentifier::btxFieldName::network` for networked fields.
 */
export type AiFieldLookupMap = Record<string, AiFieldData>;

export interface AiCellMeta {
  status: AiCellStatus;
  comment?: string;
  row?: AIExtractedRow;
}

export interface AiStatusCellRendererParams extends CellRendererParams {
  data: {
    benefit?: {
      label?: string;
      networkDependent?: boolean;
      defaultValue?: unknown;
      cell?: Record<string, unknown>;
      editableWhen?: { dependsOn: string };
    };
    benefitKey?: string;
    meta?: {
      cellDef?: unknown;
      aiStatus?: AiCellMeta;
      aiFieldLookup?: AiFieldLookupMap;
      /** True while any source document for this coverage is still processing. */
      documentProcessing?: boolean;
      benchmarkSeededPlanIds?: Record<string, boolean>;
      // benefitKeys that must never show an AI-extraction hint, even when the AI
      // field lookup has data for them (e.g. Employer Contribution once
      // broker-portal prepopulation owns that field instead of AI extraction).
      excludedAiHintBenefitKeys?: ReadonlySet<string>;
      /** Gates the validated tint. Injected per row from the app's flag read. */
      enableCellValidation?: boolean;
      /** Gates the high-confidence icon. Injected per row from the app's flag read. */
      enableHighAccuracy?: boolean;
    };
  };
}

interface StatusFlags {
  isAutoNotFound: boolean;
  isManual: boolean;
  hasComment: boolean;
  isEdited: boolean;
}

interface AiPaletteColors {
  extracted?: string;
  comment?: string;
  reviewBackground?: string;
  validatedBackground?: string;
  extractionError?: string;
  edited?: string;
}

interface CellFlags {
  needsReview: boolean;
  isValidated: boolean;
}

/**
 * Resolves the cell's background tint. The two flags name opposite states and
 * each write clears the other, so a cell holds at most one. Needs-review is
 * checked first only to settle a row that somehow carries both.
 */
export const resolveCellBackground = (
  { needsReview, isValidated }: CellFlags,
  palette: AiPaletteColors | undefined,
): string => {
  if (needsReview) return palette?.reviewBackground ?? "transparent";
  if (isValidated) return palette?.validatedBackground ?? "transparent";
  return "transparent";
};

const getStatusFlags = (status: AiCellStatus): StatusFlags => {
  const isAutoNotFound = status === "needs_review_auto";
  const isManual = status === "needs_review_manual";
  const hasComment = status === "needs_review_comment";
  const isEdited = status === "edited";

  return {
    isAutoNotFound,
    isManual,
    hasComment,
    isEdited,
  };
};

interface BlueCornerProps {
  show: boolean;
  onTriggerEnter: (e: React.MouseEvent<HTMLElement>) => void;
  onTriggerLeave: () => void;
}

const BlueCorner = ({
  show,
  onTriggerEnter,
  onTriggerLeave,
}: BlueCornerProps): React.JSX.Element | null => {
  if (!show) return null;

  return (
    <Box
      onMouseEnter={onTriggerEnter}
      onMouseLeave={onTriggerLeave}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: CORNER_SIZE,
        height: CORNER_SIZE,
        cursor: "pointer",
        zIndex: 1,
      }}
    />
  );
};

interface IconProps {
  isAutoNotFound: boolean;
  isEdited: boolean;
  isHighAccuracy: boolean;
  extractionErrorColor?: string;
  editedColor?: string;
  highAccuracyColor?: string;
}

const ICON_STYLES = {
  position: "absolute",
  top: "50%",
  left: 10,
  transform: "translateY(-50%)",
  fontSize: 16,
} as const;

const IconRenderer = ({
  isAutoNotFound,
  isEdited,
  isHighAccuracy,
  extractionErrorColor,
  editedColor,
  highAccuracyColor,
}: IconProps): React.JSX.Element | null => {
  if (isAutoNotFound) {
    return (
      <WarningAmberIcon
        data-testid="ai-cell-extraction-error-icon"
        sx={{ ...ICON_STYLES, color: extractionErrorColor }}
      />
    );
  }

  if (isEdited) {
    return (
      <EditOutlinedIcon
        data-testid="ai-cell-edited-icon"
        sx={{ ...ICON_STYLES, color: editedColor }}
      />
    );
  }

  if (isHighAccuracy) {
    return (
      <Tooltip title={HIGH_ACCURACY_TOOLTIP} placement="top" arrow>
        <WorkspacePremiumOutlinedIcon
          data-testid="ai-cell-high-accuracy-icon"
          sx={{ ...ICON_STYLES, color: highAccuracyColor }}
        />
      </Tooltip>
    );
  }

  return null;
};

interface ContentProps {
  isAutoNotFound: boolean;
  value?: string;
}

const ContentRenderer = ({
  isAutoNotFound,
  value,
}: ContentProps): React.JSX.Element => {
  if (isAutoNotFound) {
    return <Box component="span">-</Box>;
  }

  return (
    <Box
      component="span"
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value ?? "-"}
    </Box>
  );
};

interface StylesProps {
  showBlueCorner: boolean;
  backgroundColor: string;
  extractedColor?: string;
}

const getCellStyles = ({
  showBlueCorner,
  backgroundColor,
  extractedColor,
}: StylesProps): Record<string, unknown> => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: 1,
  backgroundColor,
  ...(showBlueCorner && {
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: 0,
      height: 0,
      borderStyle: "solid",
      borderWidth: `${CORNER_SIZE}px ${CORNER_SIZE}px 0 0`,
      borderColor: `${extractedColor ?? "currentColor"} transparent transparent transparent`,
    },
  }),
});

/**
 * Resolves the AI field data for the current cell by looking up the
 * btxIdentifier (from the column) and btxFieldName (from the row) in the
 * injected aiFieldLookup map.
 *
 * The lookup is pre-expanded by `buildLookup` (in useBenefitPlanFields) with
 * suffix variants (Type, Amount, Age, etc.) and network fallbacks, so this
 * function only needs to do direct key lookups.
 */
export const resolveAiField = (
  params: AiStatusCellRendererParams,
): AiFieldData | undefined => {
  const lookup = params.data?.meta?.aiFieldLookup;
  if (!lookup) return undefined;

  const colField = params.colDef?.field;
  const benefitKey = params.data?.benefitKey;
  if (!colField || !benefitKey) return undefined;

  if (params.data?.meta?.excludedAiHintBenefitKeys?.has(benefitKey)) {
    return undefined;
  }

  const parsed = parsePlanField(colField);
  if (!parsed) return undefined;

  const { planId, network } = parsed;

  // Try with network qualifier first, then without
  if (network) {
    const withNetwork = `${planId}::${benefitKey}::${network}`;
    if (lookup[withNetwork]) return lookup[withNetwork];
  }

  const withoutNetwork = `${planId}::${benefitKey}`;
  return lookup[withoutNetwork];
};

export const resolveAiClassName = (
  params: AiStatusCellRendererParams,
  aiField?: AiFieldData,
): string => aiField?.planName || params.hierarchy?.[0] || "-";

const buildAiRow = (params: AiStatusCellRendererParams): AIExtractedRow => {
  // 1. Check explicit row override (e.g., from mocks)
  if (params.data?.meta?.aiStatus?.row) {
    return params.data.meta.aiStatus.row;
  }

  // 2. Check field lookup for real AI metadata
  const aiField = resolveAiField(params);
  if (aiField) {
    return {
      className: resolveAiClassName(params, aiField),
      fieldName: params.data?.benefit?.label ?? aiField.btxFieldName,
      value: aiField.value === "NOT_FOUND" ? "-" : aiField.value,
      pdfPage: aiField.pageNumber != null ? `Page ${aiField.pageNumber}` : "-",
      pdfPageNumber: aiField.pageNumber ?? 0,
      justification: aiField.justification,
      documentDownloadUrl: aiField.documentDownloadUrl ?? undefined,
      documentName: aiField.documentName ?? undefined,
      source: aiField.source,
    };
  }

  // No AI data available for this cell
  return {
    className: resolveAiClassName(params),
    fieldName: params.data?.benefit?.label ?? "-",
    value: "-",
    pdfPage: "-",
    pdfPageNumber: 0,
    justification: "-",
  };
};

/** Extract field comment lookup to reduce component complexity. */
const useFieldCommentLookup = (
  colField: string | undefined,
  benefitKey: string | undefined,
): {
  comment: string;
  hasComment: boolean;
  needsReview: boolean;
  manuallyEdited: boolean;
  isBenchmarkField: boolean;
  isValidated: boolean;
} => {
  const { getFieldComment } = useFieldMetadata();
  const parsed = parsePlanField(colField);
  const qualifiedFieldPath = benefitKey
    ? qualifyFieldPath(benefitKey, parsed?.network)
    : undefined;
  const fieldComment =
    parsed?.planId && qualifiedFieldPath
      ? getFieldComment(parsed.planId, qualifiedFieldPath)
      : undefined;
  return {
    comment: fieldComment?.comment ?? "",
    hasComment: Boolean(fieldComment?.comment),
    needsReview: Boolean(fieldComment?.needsReview),
    manuallyEdited: Boolean(fieldComment?.manuallyEdited),
    isBenchmarkField: Boolean(fieldComment?.isBenchmarkField),
    isValidated: Boolean(fieldComment?.isValidated),
  };
};

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

// Matches a full run of digits (with optional comma grouping and decimal
// part) as one token, e.g. "1", "1,000", "1.5" — never a partial number.
const NUMBER_TOKEN_PATTERN = /\d[\d,]*(?:\.\d+)?/g;

// Matches a percentage token, e.g. "100%", "50%", "12.5%".
const PERCENT_TOKEN_PATTERN = /(\d[\d,]*(?:\.\d+)?)\s*%/g;

/**
 * True when the grid's value matches the AI's reported value, either exactly
 * or as a standalone number within it.
 */
const matchesExtractedValue = (value: unknown, aiValue: string): boolean => {
  if (!hasValue(value)) return false;

  const trimmedAiValue = aiValue.trim();
  if (String(value) === trimmedAiValue) return true;

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return false;
  if (Number(trimmedAiValue) === numericValue) return true;

  const tokens: string[] = trimmedAiValue.match(NUMBER_TOKEN_PATTERN) ?? [];
  if (
    tokens.some((token) => Number(token.replace(/,/g, "")) === numericValue)
  ) {
    return true;
  }

  // A percentage of earnings is a salary multiplier: "100% of annual earnings"
  // is a 1x multiplier, "50%" is 0.5x. Extraction emits the prose percentage
  // while the grid stores the multiplier, so reconcile them here (PB-676).
  const percentTokens: string[] =
    trimmedAiValue.match(PERCENT_TOKEN_PATTERN) ?? [];
  return percentTokens.some(
    (token) => Number(token.replace(/[%,\s]/g, "")) / 100 === numericValue,
  );
};

export const deriveAiStatus = (
  aiField: AiFieldData | undefined,
  value: unknown,
  benefit?: AiStatusCellRendererParams["data"]["benefit"],
  isDocumentProcessing?: boolean,
): AiCellStatus => {
  if (!aiField) return "ai_default";

  // A provenance-only extraction was fanned out onto a derived indicator key
  // (the spouse/child/dependent "Benefits Included" toggles): the extraction
  // belongs to a different row and can never map into this cell, so it must
  // never raise a mapping-error triangle. Surface the toggle's own derived
  // value with the AI provenance corner instead. Mirrors the SbsCellRenderer
  // exemption added in #11388, which the Build renderer never picked up.
  // A NOT_FOUND/blank extraction is not real provenance, so let it fall through
  // to the no-value handling below rather than claiming a blue corner.
  if (aiField.indicatorOnly && !aiReportedNoValue(aiField.value)) {
    return hasValue(value) ? "ai_extracted" : "ai_default";
  }

  if (aiReportedNoValue(aiField.value)) {
    // AI found nothing, so leave the cell blank regardless of any value present
    // — it is never a real extraction (empty, a grid-seeded spec default, or an
    // ingestion-coerced enum like "notIncluded"). A user's manual edit is shown
    // via the "edited" override in resolveFinalStatus, not here (CD-1504).

    // Hot-fix (jm-pb-609): when AI reported NOT_FOUND but the BTX catch-all mapper
    // populated the cell with a default value, re-selecting that same value from the
    // dropdown did not fire onCellValueChanged (AG Grid skips same-value edits),
    // so manuallyEdited was never set. The cell reverted to showing [] on every
    // confirmation, making the field appear un-editable. Returning "ai_extracted"
    // here surfaces the BTX value with the blue corner instead of [].
    // We need to discuss a better fix for this, but this is a low-risk hot-fix for now.

    if (hasValue(value) && !isGridDefault(value, benefit)) {
      return "ai_extracted";
    }
    return "";
  }

  // AI extracted a real value but nothing real landed in the cell — the value
  // could not be mapped. This is a genuine mapping error (yellow triangle). A
  // grid-seeded default doesn't count as mapped data (getValueWithDefault
  // injects the field's spec default into empty cells).
  if (isGridDefault(value, benefit)) {
    // ...unless the value equal to the default is itself the extraction: "1
    // times salary" maps to a Salary Multiplier of 1, which is also the field's
    // default. That is real AI data, so confirm it against the AI value before
    // anything else — the processing guard below would otherwise hide a genuine
    // value behind a dash for a document still marked processing (PB-676).
    if (matchesExtractedValue(value, aiField.value)) return "ai_extracted";

    // Otherwise, while a document in this coverage is still being processed, an
    // unmatched default may mean "not written yet" rather than "could not be
    // mapped": the ai-dox → BTX upsert issues several sequential writes and
    // Temporal retries it on failure, so a cell can be momentarily empty while
    // metadata for it already exists. Warning then is the false alarm DP-4063
    // reported.
    //
    // This deliberately keys off document processing state rather than the
    // coverage's BTX sync flag. That flag latches: it stays false for as long
    // as any plan in the coverage has no btx_identifier, so a single document
    // whose sync never completed suppressed the indicator on every fully-synced
    // plan in the coverage indefinitely (DP-4088). Processing state clears.
    if (isDocumentProcessing) return "ai_default";

    return "needs_review_auto";
  }

  return "ai_extracted";
};

export const deriveBenchmarkStatus = (
  value: unknown,
  manuallyEdited: boolean,
  isBenchmarkField: boolean,
): AiCellStatus => {
  if (!hasValue(value)) return "ai_default";
  if (manuallyEdited && !isBenchmarkField) return "ai_default";
  if (manuallyEdited && isBenchmarkField) return "edited";
  return "ai_extracted";
};

export const resolveSourceMode = (
  aiField: AiFieldData | undefined,
  isBenchmarkSeeded: boolean,
): FieldSourceMode => {
  if (aiField) return "ai";
  if (isBenchmarkSeeded) return "benchmark";
  return "none";
};

export const showsHighAccuracyIcon = (
  sourceMode: FieldSourceMode,
  status: AiCellStatus,
  aiField: AiFieldData | undefined,
  enabled: boolean,
): boolean =>
  enabled &&
  sourceMode === "ai" &&
  status === "ai_extracted" &&
  Boolean(aiField?.highAccuracy);

export interface StatusContext {
  sourceMode: FieldSourceMode;
  aiField: AiFieldData | undefined;
  derivedStatus: AiCellStatus;
  manuallyEdited: boolean;
  isBenchmarkField: boolean;
  value: unknown;
}

export const resolveFinalStatus = (ctx: StatusContext): AiCellStatus => {
  if (ctx.sourceMode === "benchmark") {
    return deriveBenchmarkStatus(
      ctx.value,
      ctx.manuallyEdited,
      ctx.isBenchmarkField,
    );
  }
  return ctx.manuallyEdited && ctx.aiField ? "edited" : ctx.derivedStatus;
};

interface SourceColors {
  corner?: string;
  edited?: string;
}

const resolveSourceColors = (
  sourceMode: FieldSourceMode,
  aiPalette: AiPaletteColors | undefined,
  secondaryMain: string,
): SourceColors => ({
  corner: sourceMode === "benchmark" ? secondaryMain : aiPalette?.extracted,
  edited: aiPalette?.edited,
});

const isHighAccuracyEnabled = (params: AiStatusCellRendererParams): boolean =>
  Boolean(params.data?.meta?.enableHighAccuracy);

const isBenchmarkSeededPlan = (params: AiStatusCellRendererParams): boolean => {
  const colPlanId = parsePlanField(params.colDef?.field)?.planId;
  return Boolean(
    colPlanId && params.data?.meta?.benchmarkSeededPlanIds?.[colPlanId],
  );
};

const resolveBaseStatus = (
  params: AiStatusCellRendererParams,
  aiField: AiFieldData | undefined,
): AiCellStatus =>
  params.data?.meta?.aiStatus?.status ??
  deriveAiStatus(
    aiField,
    params.value,
    params.data?.benefit,
    params.data?.meta?.documentProcessing,
  );

const sharesExtractionWithController = (
  params: AiStatusCellRendererParams,
  aiField: AiFieldData | undefined,
): boolean => {
  const controllerKey = params.data?.benefit?.editableWhen?.dependsOn;
  if (!aiField || !controllerKey) return false;

  return (
    resolveAiField({
      ...params,
      data: { ...params.data, benefitKey: controllerKey },
    }) === aiField
  );
};

const useIsAnsweredByController = (
  params: AiStatusCellRendererParams,
  aiField: AiFieldData | undefined,
): boolean => {
  const { manuallyEdited: controllerManuallyEdited } = useFieldCommentLookup(
    params.colDef?.field,
    params.data?.benefit?.editableWhen?.dependsOn,
  );
  const isEmptyAfterControllerEdit =
    controllerManuallyEdited &&
    isGridDefault(params.value, params.data?.benefit);
  return (
    sharesExtractionWithController(params, aiField) &&
    (isEmptyAfterControllerEdit || isCellDisabled(params))
  );
};

const formatCellValue = (params: AiStatusCellRendererParams): string =>
  String(params.valueFormatted ?? params.value ?? "-");

export const AiStatusCellRenderer: ComponentType<AiStatusCellRendererParams> = (
  params: AiStatusCellRendererParams,
): React.JSX.Element => {
  const theme = useTheme();
  // `schemeAiPalette` reads `theme.vars` first so the tints follow the scheme.
  const aiPalette = schemeAiPalette(theme) as AiPaletteColors | undefined;
  const aiPopover = useHoverPopover();

  const aiField = resolveAiField(params);

  // Determine source mode: AI and benchmark are mutually exclusive
  const sourceMode = resolveSourceMode(aiField, isBenchmarkSeededPlan(params));
  const derivedStatus = resolveBaseStatus(params, aiField);

  // Override with "edited" when the user manually changed an AI-extracted value
  const {
    comment,
    hasComment,
    needsReview,
    manuallyEdited,
    isBenchmarkField,
    isValidated,
  } = useFieldCommentLookup(params.colDef?.field, params.data?.benefitKey);
  const isAnsweredByController = useIsAnsweredByController(params, aiField);

  // For benchmark mode, strip grid-level defaults and commission fields
  // so they don't trigger a false-positive benchmark indicator.
  const isExcludedFromBenchmark =
    sourceMode === "benchmark" &&
    (isGridDefault(params.value, params.data?.benefit) ||
      commissionFields.includes(params.data?.benefitKey ?? ""));
  const benchmarkValue: unknown = isExcludedFromBenchmark
    ? undefined
    : params.value;

  // Derive final status based on source mode
  const status = isAnsweredByController
    ? "ai_default"
    : resolveFinalStatus({
        sourceMode,
        aiField,
        derivedStatus,
        manuallyEdited,
        isBenchmarkField,
        value: benchmarkValue,
      });

  const flags = getStatusFlags(status);
  const showCorner = status !== "ai_default";

  const { corner: cornerColor, edited: editedColor } = resolveSourceColors(
    sourceMode,
    aiPalette,
    theme.palette.brandPrimary.main,
  );

  const aiRow = buildAiRow(params);

  const cellStyles = getCellStyles({
    showBlueCorner: showCorner,
    backgroundColor: resolveCellBackground(
      {
        needsReview,
        isValidated:
          isValidated && Boolean(params.data?.meta?.enableCellValidation),
      },
      aiPalette,
    ),
    extractedColor: cornerColor,
  });

  return (
    <Box className="ai-status-cell" sx={cellStyles}>
      <BlueCorner
        show={showCorner && sourceMode === "ai"}
        onTriggerEnter={aiPopover.onTriggerEnter}
        onTriggerLeave={aiPopover.onTriggerLeave}
      />

      {hasComment && <FieldCommentCorner comment={comment} />}

      <IconRenderer
        isAutoNotFound={flags.isAutoNotFound}
        isEdited={flags.isEdited}
        isHighAccuracy={showsHighAccuracyIcon(
          sourceMode,
          status,
          aiField,
          isHighAccuracyEnabled(params),
        )}
        extractionErrorColor={aiPalette?.extractionError}
        editedColor={editedColor}
        highAccuracyColor={aiPalette?.extracted}
      />

      {params.children ?? (
        <ContentRenderer
          isAutoNotFound={flags.isAutoNotFound}
          value={formatCellValue(params)}
        />
      )}

      {sourceMode === "ai" && (
        <AIExtractedFieldCard
          row={aiRow}
          anchorEl={aiPopover.anchorEl}
          open={aiPopover.open}
          onMouseEnter={aiPopover.onCardEnter}
          onMouseLeave={aiPopover.onCardLeave}
        />
      )}
    </Box>
  );
};

AiStatusCellRenderer.displayName = "AiStatusCellRenderer";
